// Padrão das páginas (0.65): folha (tipo, cor e tema) e as 4 bolinhas de cor da caneta e do texto.
// O mais específico vale: a própria página › a aba (Ajustes › Abas › [aba] › Páginas desta aba) › o app (Ajustes › Páginas).
import type { AbaId, ConfigPaginas, Folha, TemaFolha, TipoFolha } from '../store/types';
import { getDoc, setDoc, useDoc } from '../store/store';
import { N_ } from './i18n';

export const BOLINHAS_PADRAO = ['#111827', '#2563EB', '#DC2626', '#16A34A'];
export const FOLHA_PADRAO: Folha = { tipo: 'pautada', cor: 'auto', tema: null, forca: 0.35 };
export const CONFIG_PADRAO: ConfigPaginas = { folha: FOLHA_PADRAO, bolinhas: BOLINHAS_PADRAO };

/** Abas que têm páginas com escrita (mostram "Páginas desta aba" em Ajustes › Abas). */
export const ABAS_COM_PAGINAS: AbaId[] = ['estudos', 'anotacoes', 'diario', 'documentos'];

export const TIPOS_FOLHA: [TipoFolha, string][] = [['lisa', N_('Lisa')], ['pautada', N_('Pautada')], ['margem', N_('Pautada com margem')],
  ['quadriculada', N_('Quadriculada')], ['pontilhada', N_('Pontilhada')]];
/** Cores da folha. "auto" = clara no tema claro e escura no tema escuro. */
export const CORES_FOLHA: [string, string][] = [['auto', N_('Clara (segue o tema)')], ['#FFFFFF', N_('Branco')], ['#FBF6E9', N_('Creme')],
  ['#FCE7EF', N_('Rosa')], ['#EFE7FB', N_('Lilás')], ['#E3EEFB', N_('Azul')], ['#E4F4E8', N_('Verde')], ['#2B2F38', N_('Cinza-escuro')]];
export const TEMAS_FOLHA: [TemaFolha, string][] = [['floral', N_('Floral')], ['aquarela', N_('Aquarela')], ['estrelas', N_('Céu estrelado')],
  ['folhas', N_('Folhas e plantas')], ['geometrico', N_('Geométrico')], ['infantil', N_('Infantil')], ['minimalista', N_('Minimalista')]];

const valida = (c?: Partial<ConfigPaginas> | null): ConfigPaginas => ({
  folha: { ...FOLHA_PADRAO, ...(c?.folha ?? {}) },
  bolinhas: [0, 1, 2, 3].map(i => c?.bolinhas?.[i] ?? BOLINHAS_PADRAO[i]),
});

/** Configuração que vale numa aba (personalizada ou a do app). */
export function configDaAba(aba?: AbaId | null): ConfigPaginas {
  const aj = getDoc('ajustes');
  const daAba = aba ? aj.paginasAba?.[aba] : undefined;
  return valida(daAba ?? aj.paginas);
}
export function useConfigPaginas(aba?: AbaId | null) {
  const aj = useDoc('ajustes');
  const daAba = aba ? aj.paginasAba?.[aba] : undefined;
  return { config: valida(daAba ?? aj.paginas), personalizada: !!daAba, ferramenta: aj.ferramenta ?? null, cantoBarra: aj.cantoBarra ?? 'te' };
}
export const configDoApp = () => valida(getDoc('ajustes').paginas);

export function salvarConfigApp(c: ConfigPaginas) { setDoc('ajustes', d => ({ ...d, paginas: c })); }
export function salvarConfigAba(aba: AbaId, c: ConfigPaginas | null) {
  setDoc('ajustes', d => {
    const m = { ...(d.paginasAba ?? {}) };
    if (c) m[aba] = c; else delete m[aba];
    return { ...d, paginasAba: m };
  });
}
/** Segurar uma bolinha e escolher a cor: fica guardada NAQUELA bolinha (na aba, se a aba for personalizada; senão no app). */
export function salvarBolinha(aba: AbaId | null | undefined, i: number, cor: string) {
  const aj = getDoc('ajustes');
  if (aba && aj.paginasAba?.[aba]) {
    const c = configDaAba(aba); const b = [...c.bolinhas]; b[i] = cor;
    return salvarConfigAba(aba, { ...c, bolinhas: b });
  }
  const c = configDoApp(); const b = [...c.bolinhas]; b[i] = cor;
  salvarConfigApp({ ...c, bolinhas: b });
}
/** Canto da barra flutuante na tela cheia (G2): fica guardado para as próximas vezes. */
export function salvarCantoBarra(c: 'te' | 'td' | 'be' | 'bd') { if (getDoc('ajustes').cantoBarra !== c) setDoc('ajustes', d => ({ ...d, cantoBarra: c })); }
export function salvarFerramenta(f: string) { if (getDoc('ajustes').ferramenta !== f) setDoc('ajustes', d => ({ ...d, ferramenta: f })); }

/** Cor de fundo real da folha. */
export function fundoFolha(f: Folha, escuro: boolean) {
  if (f.cor === 'auto') return escuro ? '#1F232C' : '#FFFFFF';
  return f.cor;
}
/** Folha escura (texto e linhas claros). */
export function folhaEscura(f: Folha, escuro: boolean) {
  const c = fundoFolha(f, escuro).replace('#', '');
  const n = parseInt(c.length === 3 ? c.split('').map(x => x + x).join('') : c, 16);
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  return (0.299 * r + 0.587 * g + 0.114 * b) < 128;
}
const PRETOS = ['#111827', '#000000', '#111111', '#0F172A'];
/** Regra de contraste: em folha escura, a bolinha preta vira branca, para a escrita não sumir. */
export function corNaFolha(cor: string, escura: boolean) {
  if (escura && PRETOS.map(x => x.toUpperCase()).includes(cor.toUpperCase())) return '#F5F5F5';
  return cor;
}

/** Nome da página na lista (C11): o nome escrito; sem nome, o "Título" da folha; sem os dois, "Sem nome". */
export function nomeDaPagina(nome: string, tituloFolha: string, semNome: string) {
  return nome.trim() || tituloFolha.trim() || semNome;
}
